import React, {useEffect, useRef} from "react";
import {Box, VStack} from "components-lib";

// Import modular section components
import ParametersSection from "./sections/ParametersSection"; // Fixed import
import AllergiesSection from "./sections/AllergiesSection";
import ComplaintsSection from "./sections/ComplaintsSection";
import ClinicalFindingsSection from "./sections/ClinicalFindingsSection";
import PastHistorySection from "./sections/PastHistorySection";
import DiagnosisSection from "./sections/DiagnosisSection";
import AdviceSection from "./sections/AdviceSection";

// Import custom hook for data management
import {useTextInputsData} from "./hooks/useTextInputsData";

// Import AutocompleteProvider for API-based suggestions
import {AutocompleteProvider} from "./AutocompleteProvider";

/**
 * TextInputs Component - Main container for patient data input sections
 *
 * This component has been modularized into smaller, focused components:
 * - parametersSection: Handles patient parameters (BP, pulse, weight, etc.) - conditionally rendered
 * - AllergiesSection: Manages allergy information (medicines, food, other)
 * - ComplaintsSection: Handles patient complaints with severity and duration
 * - DiagnosisSection: Manages diagnosis information
 * - AdviceSection: Handles advice management with API integration
 *
 * Data management is centralized through the useTextInputsData custom hook
 * which provides state management and synchronization across all sections.
 *
 * Parameters section is conditionally rendered based on doctor profile settings.
 */
/*
  Template data usage note:
  - `TextInputs` receives `templateData` and internally the `useTextInputsData`
  - When `templateData` is provided, `TextInputs` / the hook will map the template
    into the sections (medicines, complaints, advices, clinical findings, past history)
    and call the `onDataChange` callback which ultimately updates `patientData` via
    the setter functions provided by `usePatientData` in `PatientDetails`.

  See `docs/LOAD_TEMPLATE_FLOW.md` for full details.
*/
const TextInputs = ({
                        templateData,
                        onDataChange,
                        onAddparameters,
                        initialparameters,
                        doctorRecommendations = {
                            diagnosis: [],
                            complaints: [],
                            allergies: {
                                medicines: [],
                                food: [],
                                other: [],
                            },
                        },
                        appointment_id,
                        doctor_id,
                        showParameters = true,
                        PastpatientSpecifcData,
                        appointmentPrescription,
                        handleSubmit //parameters
                    }) => {
    const prescriptionHydratedRef = useRef(null);
    // Use custom hook to manage all data state
    const {
        // parameters data
        parametersData,
        setparametersData,
        parametersList,
        setparametersList,

        // Allergies data
        medicines,
        setMedicines,
        food,
        setFood,
        other,
        setOther,
        newMedicine,
        setNewMedicine,
        newFood,
        setNewFood,
        newOther,
        setNewOther,

        // Complaints data
        complaints,
        setComplaints,
        newComplaint,
        setNewComplaint,
        severitySuggestions,
        setSeveritySuggestions,

        // Diagnosis data
        diagnosiss,
        setDiagnosiss,
        newDiagnosis,
        setNewDiagnosis,
        diagnosisSuggestions,
        setDiagnosisSuggestions,

        // Advice data
        advicesData,
        setAdvicesData,
        newAdvice,
        setNewAdvice,
        adviceSuggestions,
        setAdviceSuggestions,
        selectedAdviceId,
        setSelectedAdviceId,

        // Clinical findings data
        clinicalFindingsData,
        setClinicalFindingsData,
        newClinicalFinding,
        setNewClinicalFinding,

        PastHistoryData,
        setPastHistoryData,
        newPastHistory,
        setNewPastHistory,
    } = useTextInputsData(initialparameters, onDataChange, onAddparameters);

    // Hydrate all text inputs once per appointment from the central appointment prescription payload
    useEffect(() => {
        if (!appointmentPrescription || !appointment_id) return;
        if (prescriptionHydratedRef.current === appointment_id) return;

        const source = appointmentPrescription.data || appointmentPrescription;
        const rx = source?.prescription || {};

        const pickArray = (key) => {
            if (Array.isArray(source?.[key])) return source[key];
            if (Array.isArray(rx?.[key])) return rx[key];
            if (Array.isArray(appointmentPrescription?.[key])) return appointmentPrescription[key];
            return [];
        };

        const normalizeStr = (val) => (val || "").toString().trim().toLowerCase();

        const mergeByKey = (existing = [], incoming = [], key) => {
            const seen = new Set(existing.map((item) => normalizeStr(item[key])));
            const toAdd = incoming.filter(
                (item) => item && item[key] && !seen.has(normalizeStr(item[key])),
            );
            if (!toAdd.length) return existing;
            return [...existing, ...toAdd];
        };

        // Complaints
        const rxComplaints = pickArray("complaints").map((c, idx) => ({
            no: c?.no ?? c?.complaint_no ?? idx + 1,
            complaint: c?.complaint || c?.complaint_text || "",
            severity: c?.severity || "",
            duration: c?.duration || "",
            date: c?.date || "",
        }));
        if (rxComplaints.length) {
            setComplaints((prev = []) => mergeByKey(prev, rxComplaints, "complaint"));
        }

        // Diagnosis
        const rxDiagnosis = pickArray("diagnosis").map((d, idx) => ({
            no: d?.no ?? d?.diagnosis_no ?? idx + 1,
            diagnosis: d?.diagnosis || d?.diagnosis_text || "",
            duration: d?.duration || "",
            date: d?.date || "",
        }));
        if (rxDiagnosis.length) {
            setDiagnosiss((prev = []) => mergeByKey(prev, rxDiagnosis, "diagnosis"));
        }

        // Advice
        const rxAdvices = (pickArray("advice").length
            ? pickArray("advice")
            : pickArray("advices")).map((a) => ({
            advice: a?.advice || a?.advice_text || "",
            id: a?.id,
            date: a?.date,
        }));
        if (rxAdvices.length) {
            setAdvicesData((prev = []) => mergeByKey(prev, rxAdvices, "advice"));
        }

        // Clinical findings
        const rxFindings = pickArray("clinical_findings").map((f) => ({
            finding: f?.finding || f?.clinical_finding || "",
        }));
        if (rxFindings.length) {
            setClinicalFindingsData((prev = []) => mergeByKey(prev, rxFindings, "finding"));
        }

        // Parameters (array -> keyed object)
        const rxParameters = pickArray("parameters");
        if (rxParameters.length) {
            const mapped = rxParameters.reduce((acc, param) => {
                const key = String(param?.parameter_id ?? param?.id ?? "");
                if (!key) return acc;
                const value = param?.value ?? param?.parameter_value ?? param?.reading;
                if (value !== undefined && value !== null) {
                    acc[key] = String(value);
                }
                return acc;
            }, {});

            if (Object.keys(mapped).length) {
                setparametersData((prev = {}) => ({
                    ...prev,
                    ...mapped,
                }));
            }
        }

        prescriptionHydratedRef.current = appointment_id;
    }, [
        appointmentPrescription,
        appointment_id,
        setAdvicesData,
        setClinicalFindingsData,
        setComplaints,
        setDiagnosiss,
        setparametersData,
    ]);


    // useEffect(() => {
    //   console.log(
    //     "🔴 Template Data Recived at Text Input section  " +
    //       JSON.stringify(templateData)
    //   );
    // }, [templateData]);

    return (
        <AutocompleteProvider doctorId={doctor_id}>
            <VStack spacing={6} align="stretch">
                <Box borderRadius="lg" overflow="hidden">
                    <table
                        style={{
                            borderCollapse: "collapse",
                            width: "100%",
                            background: "white",
                            marginBottom: "6px",
                        }}
                    >
                        <tbody>
                        {/* Parameters Section - Only show if enabled for this doctor */}
                        {showParameters && (
                            <ParametersSection
                                parametersData={parametersData}
                                setparametersData={setparametersData}
                                parametersList={parametersList}
                                setparametersList={setparametersList}
                                onAddparameters={onAddparameters}
                                appointment_id={appointment_id}
                                doctor_id={doctor_id}
                                appointmentPrescription={appointmentPrescription}
                                handleSubmit //parameters
                            />
                        )}
                        {/* Allergies Section with Enhanced Autocomplete */}
                        <AllergiesSection
                            PastpatientSpecifcData={PastpatientSpecifcData}
                            appointmentPrescription={appointmentPrescription}
                            appointmentId={appointment_id}
                            medicines={medicines}
                            setMedicines={setMedicines}
                            food={food}
                            setFood={setFood}
                            other={other}
                            setOther={setOther}
                            newMedicine={newMedicine}
                            setNewMedicine={setNewMedicine}
                            newFood={newFood}
                            setNewFood={setNewFood}
                            newOther={newOther}
                            setNewOther={setNewOther}
                            doctorRecommendations={doctorRecommendations}
                        />
                        {/* Complaints Section with Enhanced Autocomplete */}
                        <ComplaintsSection
                            templateData={templateData}
                            appointmentPrescription={appointmentPrescription}
                            appointmentId={appointment_id}
                            complaints={complaints}
                            setComplaints={setComplaints}
                            newComplaint={newComplaint}
                            setNewComplaint={setNewComplaint}
                            severitySuggestions={severitySuggestions}
                            setSeveritySuggestions={setSeveritySuggestions}
                            doctorRecommendations={doctorRecommendations}
                            doctor_id={doctor_id}
                        />
                        {/* Clinical Findings Section */}
                        <ClinicalFindingsSection
                            templateData={templateData}
                            appointmentPrescription={appointmentPrescription}
                            appointmentId={appointment_id}
                            clinicalFindingsData={clinicalFindingsData}
                            setClinicalFindingsData={setClinicalFindingsData}
                            newClinicalFinding={newClinicalFinding}
                            setNewClinicalFinding={setNewClinicalFinding}
                        />
                        {/* Past History Section */}
                        <PastHistorySection
                            PastpatientSpecifcData={PastpatientSpecifcData}
                            appointmentId={appointment_id}
                            PastHistoryData={PastHistoryData}
                            setPastHistoryData={setPastHistoryData}
                            newPastHistory={newPastHistory}
                            setNewPastHistory={setNewPastHistory}
                        />
                        {/* Diagnosis Section with Enhanced Autocomplete */}
                        <DiagnosisSection
                            templateData={templateData}
                            appointmentPrescription={appointmentPrescription}
                            appointmentId={appointment_id}
                            diagnosiss={diagnosiss}
                            setDiagnosiss={setDiagnosiss}
                            newDiagnosis={newDiagnosis}
                            setNewDiagnosis={setNewDiagnosis}
                            diagnosisSuggestions={diagnosisSuggestions}
                            setDiagnosisSuggestions={setDiagnosisSuggestions}
                            doctorRecommendations={doctorRecommendations}
                            doctor_id={doctor_id}
                        />
                        {/* Advice Section with Enhanced Autocomplete */}
                        <AdviceSection
                            templateData={templateData}
                            appointmentPrescription={appointmentPrescription}
                            appointmentId={appointment_id}
                            advicesData={advicesData}
                            setAdvicesData={setAdvicesData}
                            newAdvice={newAdvice}
                            setNewAdvice={setNewAdvice}
                            selectedAdviceId={selectedAdviceId}
                            setSelectedAdviceId={setSelectedAdviceId}
                            adviceSuggestions={adviceSuggestions}
                            setAdviceSuggestions={setAdviceSuggestions}
                            doctor_id={doctor_id}
                        />
                        </tbody>
                    </table>
                </Box>
            </VStack>
        </AutocompleteProvider>
    );
};

export default TextInputs;
